import { useMemo } from 'react';
import type { AppData } from '../../data/types';
import { cn } from '../../lib/cn';
import { formatCurrency } from '../../lib/currency';
import { forecastCashflow } from '../../lib/forecast';

type Props = {
  data: AppData;
  today: Date;
};

const HORIZONS: Array<{ label: string; days: number }> = [
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
];

/**
 * Forward-looking cashflow card. Three columns (7 / 14 / 30 days), each
 * showing the projected net based on scheduled income/expenses + future
 * spread contributions. Colored by sign.
 */
export function CashflowForecast({ data, today }: Props) {
  const projections = useMemo(
    () => HORIZONS.map((h) => ({ ...h, ...forecastCashflow(data, today, h.days) })),
    [data, today],
  );

  // No scheduled items + no spreads → all three are zero. Hide the
  // section entirely so it doesn't look like dead data.
  const allZero = projections.every(
    (p) => p.income === 0 && p.expenses === 0,
  );
  if (allZero) return null;

  const currency = data.profile.currency;

  return (
    <section className="mb-section-gap">
      <h2 className="text-[17px] font-semibold text-text-primary mb-3">
        Cash flow forecast
      </h2>
      <div className="overflow-x-auto scrollbar-hide">
        <div
          className="flex flex-row gap-3 pb-1"
          style={{ width: 'max-content' }}
        >
          {projections.map((p) => {
            const tone =
              p.net > 0
                ? 'text-positive'
                : p.net < 0
                  ? 'text-negative'
                  : 'text-text-tertiary';
            const sign = p.net > 0 ? '+' : p.net < 0 ? '-' : '';
            return (
              <div
                key={p.label}
                className="w-[150px] flex-shrink-0 h-[112px] rounded-2xl p-4 bg-white dark:bg-[#18181B] border border-zinc-200/60 dark:border-zinc-800 flex flex-col justify-between"
              >
                <div className="text-[12px] font-medium text-text-tertiary">
                  {p.label}
                </div>
                <div>
                  <div
                    className={cn(
                      'text-[22px] font-light font-mono tabular-nums leading-none',
                      tone,
                    )}
                  >
                    {sign}
                    {formatCurrency(Math.abs(p.net), { currency })}
                  </div>
                  <div className="mt-1.5 text-[11px] font-mono tabular-nums leading-tight text-text-secondary">
                    <span className="text-positive">+{formatCurrency(p.income, { currency })}</span>
                    {' / '}
                    <span className="text-negative">-{formatCurrency(p.expenses, { currency })}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
